// =============================================================================
// TENANT THEME SYSTEM ("SKIN")
// Preset color schemes + custom color support for white-label branding.
//
// =====================  SKIN CONTRACT  =====================
// Every CSS color in this codebase MUST go through the variables defined by
// applyThemeToDocument() below. NEVER write a literal hex / rgb in component
// CSS or inline styles. If you need a color we don't yet expose, add it as a
// field on TenantTheme + applyThemeToDocument(), give every preset a value,
// then reference it via var(--color-*) in your component.
//
// Why: a tenant ("skin") swap should be a one-object change in this file.
// New tenant = new entry in THEME_PRESETS. That's it. No CSS files, no
// per-tenant components, no recompiles.
//
// Enforcement:
//   - To find existing violations:  npm run skin:check
//   - To auto-fix common literals:  npm run skin:fix
// ============================================================
// =============================================================================

import { applyBlissClientColorTokens } from './blissPalette';

export interface TenantTheme {
  /** Display name for the preset */
  label: string;
  /** Primary accent color (replaces gold). Used for buttons, focus rings,
   *  active states, brand accents. */
  primary: string;
  /** Lighter variant of primary */
  primaryLight: string;
  /** Darker variant of primary */
  primaryDark: string;
  /** RGB triplet for primary (for rgba() usage) */
  primaryRgb: string;
  /** Darkest background (replaces navy). The page-level surface. */
  bgDark: string;
  /** Mid-tone background (replaces charcoal). Cards on top of bgDark. */
  bgMid: string;
  /** Lighter surface background (replaces charcoal-light). Elevated cards
   *  / modals on top of bgMid. */
  bgLight: string;
  /** Primary text color (headings, main copy). Default works on every dark
   *  preset; override if you go light or want a custom tone. */
  textPrimary?: string;
  /** Secondary text (sub-headers, body when not primary). */
  textSecondary?: string;
  /** Muted text (placeholder, label, helper text). */
  textMuted?: string;
  /** Default border color for cards / dividers. */
  border?: string;
  /** Subtle divider color (e.g. between rows). */
  borderSubtle?: string;
  /** Light / dark mode the preset is designed for. Drives the design-system
   *  token defaults written in applyThemeToDocument. Presets without a value
   *  are treated as 'dark' for back-compat with the existing 13 entries. */
  mode?: 'light' | 'dark';
  /** Highest-contrast foreground on this theme's surfaces. White on dark
   *  presets, ink on light presets. Consumed as --color-ink. */
  ink?: string;
  /** RGB triplet for `ink`, so translucent hairlines/tints flip with mode. */
  inkRgb?: string;
  /** Foreground drawn on top of the primary accent (e.g. label on a gold
   *  button). Defaults to bgDark on dark presets, which is what the legacy
   *  `color: var(--color-navy)` on gold buttons resolved to. */
  onPrimary?: string;
  /** Foreground for the call sites that spelled that same thing as literal
   *  white. Kept separate from onPrimary so dark presets keep the white they
   *  were authored with, while light presets get ink. */
  onAccent?: string;
  /** RGB triplet used by translucent "sunken"/shadow layers. */
  sinkRgb?: string;
  /** Multiplier applied to sunken/shadow alphas. Dark presets keep the
   *  authored alpha (1); light presets damp it so a 20% scrim authored for a
   *  black card reads as a soft tint on pearl instead of a gray slab. */
  sinkAlpha?: string;
  /** Dimmest label text. Reads lighter than the page on dark presets and
   *  lighter than the body copy on light presets. */
  gray?: string;
  /** Secondary body text — one step more prominent than `gray`. */
  grayLight?: string;
  /** Realtimax Design Language tokens — optional per-preset overrides. When
   *  omitted, applyThemeToDocument falls back to the mode's defaults below.
   *  These are the JobTread-grade design tokens consumed by the UI primitives
   *  in src/components/ui/ and by the page builder chrome. */
  surfacePage?: string;
  surfaceCard?: string;
  surfaceElevated?: string;
  surfaceSunken?: string;
  textHeadline?: string;
  textBody?: string;
  textMutedDS?: string;
  textFaint?: string;
  borderSubtleDS?: string;
  borderDefault?: string;
  borderStrong?: string;
  shadowCard?: string;
  shadowCardHover?: string;
  shadowOverlay?: string;
}

// Sensible cross-preset defaults for the optional text/border tokens. These
// are slate tones that read well on every preset's dark backgrounds. A
// tenant who wants a different look just sets the field on its preset.
const DEFAULT_TEXT_PRIMARY = '#e2e8f0';
const DEFAULT_TEXT_SECONDARY = '#cbd5e1';
const DEFAULT_TEXT_MUTED = '#94a3b8';
const DEFAULT_BORDER = '#334155';
const DEFAULT_BORDER_SUBTLE = 'rgba(255, 255, 255, 0.06)';

// Pearl & gold surface constants. These are the only literal colors allowed
// to describe the back-office stage; everything else references them through
// the preset below or through toPearlSurface().
export const PEARL_PAGE = '#f7f3ea';
export const PEARL_CARD = '#ffffff';
export const PEARL_ELEVATED = '#fffdf9';
export const PEARL_SUNKEN = '#f0e9dc';
export const PEARL_INK = '#2e2418';
export const PEARL_INK_RGB = '46, 36, 24';

export type ThemePresetId =
  | 'realtimax'
  | 'realtimax-light'
  | 'classic-gold'
  | 'pearl-gold'
  | 'ocean-blue'
  | 'forest-green'
  | 'crimson'
  | 'royal-purple'
  | 'sunset-orange'
  | 'teal'
  | 'slate'
  | 'rose'
  | 'emerald'
  | 'maroon-cream';

/**
 * Default theme used pre-auth (login, landing) and as a fallback when a
 * tenant has no theme_preset / primary_color set. Realtimax is the umbrella
 * brand, so its orange-on-slate look is the safe default. Tenants that want
 * a different look (Bliss → classic-gold, RF → maroon-cream) opt in either
 * via tenants.theme_preset in the DB or via the slug-based lock in
 * TenantContext.
 */
export const DEFAULT_THEME_PRESET: ThemePresetId = 'realtimax';

export const THEME_PRESETS: Record<ThemePresetId, TenantTheme> = {
  // Realtimax umbrella brand. Orange accent on slate-dark stage with sand
  // mid-tones — pulled directly from the realtimax.com hero page palette
  // (--orange #E8922A, --slate-dark #1E2532, --sand #EDEAE4 family). This is
  // the global default for pre-auth screens and any tenant that hasn't been
  // assigned a theme yet.
  'realtimax': {
    label: 'Realtimax (Orange & Slate)',
    primary: '#E8922A',
    primaryLight: '#F0A84A',
    primaryDark: '#C47820',
    primaryRgb: '232, 146, 42',
    bgDark: '#1E2532',
    bgMid: '#2D3748',
    bgLight: '#4A5568',
    mode: 'dark',
  },
  // Realtimax light — the JobTread-grade enterprise-polish counterpart of the
  // umbrella brand. Same orange accent, but on a clean white-and-fog stage with
  // dark-navy headlines. Used for tenants that prefer the bright, premium SaaS
  // aesthetic (Notion / Linear / JobTread). Both presets share Realtimax brand
  // colors so the platform identity stays consistent regardless of mode.
  'realtimax-light': {
    label: 'Realtimax (Light)',
    primary: '#E8922A',
    primaryLight: '#F0A84A',
    primaryDark: '#C47820',
    primaryRgb: '232, 146, 42',
    bgDark: '#F8F9FB',
    bgMid: '#FFFFFF',
    bgLight: '#FFFFFF',
    mode: 'light',
    textPrimary: '#0F1B2D',
    textSecondary: '#4B5563',
    textMuted: '#6B7280',
    border: '#E5E7EB',
    borderSubtle: '#EEF0F3',
  },
  'classic-gold': {
    label: 'Black & Gold',
    primary: '#c9a959',
    primaryLight: '#d4b86a',
    primaryDark: '#a8893f',
    primaryRgb: '201, 169, 89',
    bgDark: '#000000',
    bgMid: '#0f0f0f',
    bgLight: '#1a1a1a',
  },
  // Pearl & Gold — the light counterpart of classic-gold and the standard
  // look for every internal back-office surface (sales, operations, admin,
  // millworks, partner studio). Pearl is the page, white is the card, and
  // gold is used only as an accent: buttons, active tabs, focus rings,
  // section labels. No black or near-black is allowed as a surface here.
  'pearl-gold': {
    label: 'Pearl & Gold',
    primary: '#b8943f',
    primaryLight: '#d4b86a',
    primaryDark: '#8a7030',
    primaryRgb: '184, 148, 63',
    bgDark: PEARL_PAGE,
    bgMid: PEARL_CARD,
    bgLight: PEARL_ELEVATED,
    mode: 'light',
    textPrimary: PEARL_INK,
    textSecondary: '#4a4034',
    textMuted: '#7a6e5e',
    border: '#e3dac9',
    borderSubtle: 'rgba(46, 36, 24, 0.09)',
    surfacePage: PEARL_PAGE,
    surfaceCard: PEARL_CARD,
    surfaceElevated: PEARL_ELEVATED,
    surfaceSunken: PEARL_SUNKEN,
  },
  'ocean-blue': {
    label: 'Ocean Blue',
    primary: '#4A9BD9',
    primaryLight: '#6BB0E5',
    primaryDark: '#3578B0',
    primaryRgb: '74, 155, 217',
    bgDark: '#0B1929',
    bgMid: '#132238',
    bgLight: '#1C2D47',
  },
  'forest-green': {
    label: 'Forest Green',
    primary: '#5B9A6F',
    primaryLight: '#74B088',
    primaryDark: '#47804A',
    primaryRgb: '91, 154, 111',
    bgDark: '#0F1E15',
    bgMid: '#182B1F',
    bgLight: '#22382A',
  },
  'crimson': {
    label: 'Crimson',
    primary: '#C95959',
    primaryLight: '#D97676',
    primaryDark: '#A84040',
    primaryRgb: '201, 89, 89',
    bgDark: '#1E0F0F',
    bgMid: '#2E1A1A',
    bgLight: '#3D2525',
  },
  'royal-purple': {
    label: 'Royal Purple',
    primary: '#8B6FC0',
    primaryLight: '#A48BD0',
    primaryDark: '#6E52A3',
    primaryRgb: '139, 111, 192',
    bgDark: '#150F28',
    bgMid: '#1F1838',
    bgLight: '#2A2248',
  },
  'sunset-orange': {
    label: 'Sunset Orange',
    primary: '#E8922A',
    primaryLight: '#F0A84A',
    primaryDark: '#C47820',
    primaryRgb: '232, 146, 42',
    bgDark: '#1E1508',
    bgMid: '#2E2010',
    bgLight: '#3D2C18',
  },
  'teal': {
    label: 'Teal',
    primary: '#4ABFBF',
    primaryLight: '#6DD0D0',
    primaryDark: '#38A0A0',
    primaryRgb: '74, 191, 191',
    bgDark: '#0B1E1E',
    bgMid: '#142B2B',
    bgLight: '#1E3838',
  },
  'slate': {
    label: 'Slate',
    primary: '#7A8B9A',
    primaryLight: '#96A5B2',
    primaryDark: '#5F7080',
    primaryRgb: '122, 139, 154',
    bgDark: '#121820',
    bgMid: '#1A222C',
    bgLight: '#242E3A',
  },
  'rose': {
    label: 'Rose',
    primary: '#D4728C',
    primaryLight: '#E08DA4',
    primaryDark: '#B85A74',
    primaryRgb: '212, 114, 140',
    bgDark: '#1E0F14',
    bgMid: '#2E1820',
    bgLight: '#3D222D',
  },
  'emerald': {
    label: 'Emerald',
    primary: '#50C878',
    primaryLight: '#6FD694',
    primaryDark: '#3AAF5F',
    primaryRgb: '80, 200, 120',
    bgDark: '#0B1E12',
    bgMid: '#14291B',
    bgLight: '#1E3726',
  },
  // Renovation Flooring brand. Rich warm-parchment stage with maroon accents.
  // Backgrounds are visibly warm chocolate/amber-brown — not near-black —
  // so the site reads as warm and premium. Creme text (#F2E4C4) dominates.
  // Maroon (#8B1C2C) is the surgical accent: buttons, active states, borders.
  'maroon-cream': {
    label: 'Creme & Maroon',
    primary: '#8B1C2C',
    primaryLight: '#A82334',
    primaryDark: '#6A1522',
    primaryRgb: '139, 28, 44',
    bgDark: '#2C1A08',
    bgMid: '#3E2510',
    bgLight: '#513016',
    textPrimary: '#F2E4C4',
    textSecondary: '#DECEA8',
    textMuted: '#BCA882',
    border: '#5E3A1E',
    borderSubtle: 'rgba(242, 228, 196, 0.09)',
  },
};

/**
 * Converts a hex color string to an "R, G, B" triplet string for use in rgba().
 */
export function hexToRgb(hex: string): string {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

/**
 * Lightens a hex color by a given amount (0-1).
 */
function lighten(hex: string, amount: number): string {
  const cleaned = hex.replace('#', '');
  const r = Math.min(255, Math.round(parseInt(cleaned.substring(0, 2), 16) + (255 - parseInt(cleaned.substring(0, 2), 16)) * amount));
  const g = Math.min(255, Math.round(parseInt(cleaned.substring(2, 4), 16) + (255 - parseInt(cleaned.substring(2, 4), 16)) * amount));
  const b = Math.min(255, Math.round(parseInt(cleaned.substring(4, 6), 16) + (255 - parseInt(cleaned.substring(4, 6), 16)) * amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Darkens a hex color by a given amount (0-1).
 */
function darken(hex: string, amount: number): string {
  const cleaned = hex.replace('#', '');
  const r = Math.round(parseInt(cleaned.substring(0, 2), 16) * (1 - amount));
  const g = Math.round(parseInt(cleaned.substring(2, 4), 16) * (1 - amount));
  const b = Math.round(parseInt(cleaned.substring(4, 6), 16) * (1 - amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Generates a dark background color that complements the given primary color.
 * Picks up the primary hue at very low saturation/lightness.
 */
function generateBgFromPrimary(hex: string, lightness: number): string {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  // Blend primary with near-black, weighted heavily toward black
  const weight = 0.12;
  const base = lightness;
  const nr = Math.round(r * weight + base);
  const ng = Math.round(g * weight + base);
  const nb = Math.round(b * weight + base);
  return `#${Math.min(255, nr).toString(16).padStart(2, '0')}${Math.min(255, ng).toString(16).padStart(2, '0')}${Math.min(255, nb).toString(16).padStart(2, '0')}`;
}

/**
 * Resolves a theme from preset ID and optional custom primary color.
 * If customPrimary is provided, it generates a full theme from that color.
 * Falls back to classic-gold if the preset is unknown.
 */
export function resolveTheme(
  presetId?: string | null,
  customPrimary?: string | null
): TenantTheme {
  // Custom primary color overrides everything — generate full palette
  if (customPrimary && /^#[0-9a-fA-F]{6}$/.test(customPrimary)) {
    return {
      label: 'Custom',
      primary: customPrimary,
      primaryLight: lighten(customPrimary, 0.2),
      primaryDark: darken(customPrimary, 0.18),
      primaryRgb: hexToRgb(customPrimary),
      bgDark: generateBgFromPrimary(customPrimary, 10),
      bgMid: generateBgFromPrimary(customPrimary, 22),
      bgLight: generateBgFromPrimary(customPrimary, 32),
    };
  }

  // Use preset — fall back to the realtimax umbrella brand if presetId is
  // null/unknown (which is what happens pre-auth and for tenants that
  // haven't picked a theme yet).
  const key = (presetId || DEFAULT_THEME_PRESET) as ThemePresetId;
  return THEME_PRESETS[key] ?? THEME_PRESETS[DEFAULT_THEME_PRESET];
}

// =============================================================================
// REGION COLORS — per-zone background overrides (Elementor-style)
// Stored in tenants.settings.regionColors (JSONB).
// Each field maps to a CSS custom property; unset fields remove the var so
// the theme-level fallback (--color-navy / --color-charcoal) takes over.
// =============================================================================

export interface RegionColors {
  /** Top header / banner bar */
  banner?: string;
  /** Macro tab navigation strip */
  navbar?: string;
  /** Full page background */
  page?: string;
  /** Main content area wrapper */
  content?: string;
  /** Left / right sidebar panels */
  sidebar?: string;
  /** Footer / bottom drawers */
  footer?: string;
}

const REGION_CSS_VARS: Record<keyof RegionColors, string> = {
  banner:  '--color-region-banner',
  navbar:  '--color-region-navbar',
  page:    '--color-region-page',
  content: '--color-region-content',
  sidebar: '--color-region-sidebar',
  footer:  '--color-region-footer',
};

/**
 * Applies per-region background colors to the document root.
 * Pass null / undefined to clear all region overrides (e.g. on theme change).
 */
export function applyRegionColorsToDocument(regionColors: RegionColors | null | undefined): void {
  const root = document.documentElement;
  (Object.keys(REGION_CSS_VARS) as (keyof RegionColors)[]).forEach((key) => {
    const cssVar = REGION_CSS_VARS[key];
    const value = regionColors?.[key];
    if (value && /^#[0-9a-fA-F]{6}$/.test(value)) {
      root.style.setProperty(cssVar, value);
    } else {
      root.style.removeProperty(cssVar);
    }
  });
}

// =============================================================================
// REALTIMAX DESIGN LANGUAGE — per-mode design-system defaults
//
// JobTread-grade tokens for the admin chrome (page builder, home tab, future
// admin tabs). Each preset declares a `mode`; applyThemeToDocument writes the
// matching defaults below, overridable per-preset via the design-token fields
// on TenantTheme. Pill colors are fixed across modes so categories stay
// recognizable everywhere.
// =============================================================================

const DESIGN_DEFAULTS = {
  light: {
    surfacePage:     '#F6F7F9',
    surfaceCard:     '#FFFFFF',
    surfaceElevated: '#FFFFFF',
    surfaceSunken:   '#EEF0F3',
    textHeadline:    '#0F1B2D',
    textBody:        '#374151',
    textMuted:       '#6B7280',
    textFaint:       '#9CA3AF',
    borderSubtle:    '#EEF0F3',
    borderDefault:   '#E5E7EB',
    borderStrong:    '#D1D5DB',
    shadowCard:      '0 1px 2px rgba(15, 27, 45, 0.04), 0 1px 3px rgba(15, 27, 45, 0.04)',
    shadowCardHover: '0 8px 24px rgba(15, 27, 45, 0.08), 0 2px 6px rgba(15, 27, 45, 0.04)',
    shadowOverlay:   '0 24px 56px rgba(15, 27, 45, 0.18)',
  },
  dark: {
    surfacePage:     '#0E1117',
    surfaceCard:     '#161B22',
    surfaceElevated: '#1E242D',
    surfaceSunken:   '#0A0D12',
    textHeadline:    '#F5F6F8',
    textBody:        'rgba(245, 246, 248, 0.78)',
    textMuted:       'rgba(245, 246, 248, 0.55)',
    textFaint:       'rgba(245, 246, 248, 0.32)',
    borderSubtle:    'rgba(255, 255, 255, 0.06)',
    borderDefault:   'rgba(255, 255, 255, 0.10)',
    borderStrong:    'rgba(255, 255, 255, 0.18)',
    shadowCard:      '0 1px 2px rgba(0, 0, 0, 0.4)',
    shadowCardHover: '0 12px 32px rgba(0, 0, 0, 0.45)',
    shadowOverlay:   '0 24px 56px rgba(0, 0, 0, 0.55)',
  },
};

// Legacy-surface defaults per mode. These back the tokens that older portal
// CSS/inline styles lean on (--color-ink, --color-gray*, --color-sink-*).
//
// The dark column reproduces exactly what those call sites used to hardcode
// (#ffffff text, rgba(255,255,255,a) hairlines, rgba(0,0,0,a) scrims), so a
// dark tenant renders byte-identically after the codemod. The light column is
// what makes the same markup legible on pearl without per-component edits.
const SURFACE_DEFAULTS = {
  light: {
    ink: PEARL_INK,
    inkRgb: PEARL_INK_RGB,
    onPrimary: PEARL_INK,
    onAccent: PEARL_INK,
    sinkRgb: PEARL_INK_RGB,
    sinkAlpha: '0.22',
    gray: '#8a7f6f',
    grayLight: '#574b3c',
  },
  dark: {
    ink: '#ffffff',
    inkRgb: '255, 255, 255',
    onPrimary: '',
    onAccent: '#ffffff',
    sinkRgb: '0, 0, 0',
    sinkAlpha: '1',
    gray: '#8a8a9a',
    grayLight: '#b8b8c8',
  },
};

// Pill palette is mode-independent — these are the bright JobTread category
// chip colors. Each pill uses a darker fill for contrast against white text.
const PILL_PALETTE = {
  cyan:   '#00B8D9',
  purple: '#6D4AFF',
  orange: '#FF6B35',
  green:  '#10B981',
  blue:   '#3B82F6',
  rose:   '#EC4899',
  slate:  '#475569',
} as const;

/**
 * Applies a TenantTheme to the document root as CSS custom properties.
 * This is the ONLY mechanism for setting tenant colors at runtime — every
 * component should reference var(--color-*) / var(--surface-*) / var(--text-*)
 * from this list, never literals.
 *
 * Tokens written (legacy + new design-system layer):
 *   Legacy (kept for back-compat):
 *     --color-gold*, --color-navy, --color-charcoal*, --color-text-*,
 *     --color-border*, --shadow-button, --focus-ring
 *   New design-system layer (Realtimax Design Language):
 *     --surface-page, --surface-card, --surface-elevated, --surface-sunken
 *     --text-headline, --text-body, --text-muted-ds, --text-faint
 *     --border-subtle-ds, --border-default, --border-strong
 *     --shadow-card, --shadow-card-hover, --shadow-overlay
 *     --pill-cyan, --pill-purple, --pill-orange, --pill-green,
 *     --pill-blue, --pill-rose, --pill-slate
 *     --radius-sm (6), --radius-md (10), --radius-lg (14)
 *     --space-1..--space-8 (4 → 48 px scale)
 *     --mode ('light' | 'dark') — readable from prefers-color-scheme helpers
 *
 * Back-office routes (see BACK_OFFICE_PATHS) are re-surfaced onto pearl/white
 * before any token is written, so staff tools are uniform regardless of which
 * tenant skin is active.
 */
export function applyThemeToDocument(inputTheme: TenantTheme): void {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const backOffice = isBackOfficeSurface(pathname);
  const theme = backOffice ? toPearlSurface(inputTheme) : inputTheme;
  const root = document.documentElement;
  const mode: 'light' | 'dark' = theme.mode ?? 'dark';
  const d = DESIGN_DEFAULTS[mode];
  const s = SURFACE_DEFAULTS[mode];

  // Propagate background to body and #root so no browser-default gap
  // shows around the page when the tenant theme changes --color-navy.
  document.body.style.backgroundColor = theme.bgDark;
  document.body.style.margin = '0';
  const rootEl = document.getElementById('root');
  if (rootEl) rootEl.style.backgroundColor = theme.bgDark;

  // ---------- Legacy brand tokens (back-compat) ----------
  root.style.setProperty('--color-gold', theme.primary);
  root.style.setProperty('--color-gold-light', theme.primaryLight);
  root.style.setProperty('--color-gold-dark', theme.primaryDark);
  root.style.setProperty('--color-gold-rgb', theme.primaryRgb);

  root.style.setProperty('--color-navy', theme.bgDark);
  root.style.setProperty('--color-charcoal', theme.bgMid);
  root.style.setProperty('--color-charcoal-light', theme.bgLight);

  root.style.setProperty('--color-text-primary', theme.textPrimary ?? DEFAULT_TEXT_PRIMARY);
  root.style.setProperty('--color-text-secondary', theme.textSecondary ?? DEFAULT_TEXT_SECONDARY);
  root.style.setProperty('--color-text-muted', theme.textMuted ?? DEFAULT_TEXT_MUTED);
  root.style.setProperty('--color-border', theme.border ?? DEFAULT_BORDER);
  root.style.setProperty('--color-border-subtle', theme.borderSubtle ?? DEFAULT_BORDER_SUBTLE);

  // ---------- Mode-aware legacy surface tokens ----------
  // --color-ink replaces the old literal white text / white hairlines, and
  // --color-sink-* replaces the old literal black scrims and shadows. Both
  // resolve to the original literals in dark mode.
  root.style.setProperty('--color-ink', theme.ink ?? s.ink);
  root.style.setProperty('--color-ink-rgb', theme.inkRgb ?? s.inkRgb);
  root.style.setProperty('--color-on-primary', theme.onPrimary ?? s.onPrimary ?? theme.bgDark);
  root.style.setProperty('--color-on-accent', theme.onAccent ?? s.onAccent);
  root.style.setProperty('--color-sink-rgb', theme.sinkRgb ?? s.sinkRgb);
  root.style.setProperty('--color-sink-alpha', theme.sinkAlpha ?? s.sinkAlpha);
  root.style.setProperty('--color-gray', theme.gray ?? s.gray);
  root.style.setProperty('--color-gray-light', theme.grayLight ?? s.grayLight);

  root.style.setProperty('--shadow-button', `0 8px 30px rgba(${theme.primaryRgb}, 0.3)`);
  root.style.setProperty('--focus-ring', `0 0 0 3px rgba(${theme.primaryRgb}, 0.4)`);

  // ---------- Realtimax Design Language tokens ----------
  root.style.setProperty('--mode', mode);
  root.dataset.mode = mode;

  root.style.setProperty('--surface-page',     theme.surfacePage     ?? d.surfacePage);
  root.style.setProperty('--surface-card',     theme.surfaceCard     ?? d.surfaceCard);
  root.style.setProperty('--surface-elevated', theme.surfaceElevated ?? d.surfaceElevated);
  root.style.setProperty('--surface-sunken',   theme.surfaceSunken   ?? d.surfaceSunken);

  root.style.setProperty('--text-headline',    theme.textHeadline    ?? d.textHeadline);
  root.style.setProperty('--text-body',        theme.textBody        ?? d.textBody);
  root.style.setProperty('--text-muted-ds',    theme.textMutedDS     ?? d.textMuted);
  root.style.setProperty('--text-faint',       theme.textFaint       ?? d.textFaint);

  root.style.setProperty('--border-subtle-ds', theme.borderSubtleDS  ?? d.borderSubtle);
  root.style.setProperty('--border-default',   theme.borderDefault   ?? d.borderDefault);
  root.style.setProperty('--border-strong',    theme.borderStrong    ?? d.borderStrong);

  root.style.setProperty('--shadow-card',       theme.shadowCard       ?? d.shadowCard);
  root.style.setProperty('--shadow-card-hover', theme.shadowCardHover  ?? d.shadowCardHover);
  root.style.setProperty('--shadow-overlay',    theme.shadowOverlay    ?? d.shadowOverlay);

  // Pill palette (mode-independent)
  root.style.setProperty('--pill-cyan',   PILL_PALETTE.cyan);
  root.style.setProperty('--pill-purple', PILL_PALETTE.purple);
  root.style.setProperty('--pill-orange', PILL_PALETTE.orange);
  root.style.setProperty('--pill-green',  PILL_PALETTE.green);
  root.style.setProperty('--pill-blue',   PILL_PALETTE.blue);
  root.style.setProperty('--pill-rose',   PILL_PALETTE.rose);
  root.style.setProperty('--pill-slate',  PILL_PALETTE.slate);

  // Radius + spacing scale (constants, but written so theme-aware consumers
  // can rely on the variables instead of literal values).
  root.style.setProperty('--radius-sm', '6px');
  root.style.setProperty('--radius-md', '10px');
  root.style.setProperty('--radius-lg', '14px');
  root.style.setProperty('--space-1', '4px');
  root.style.setProperty('--space-2', '8px');
  root.style.setProperty('--space-3', '12px');
  root.style.setProperty('--space-4', '16px');
  root.style.setProperty('--space-5', '20px');
  root.style.setProperty('--space-6', '24px');
  root.style.setProperty('--space-7', '32px');
  root.style.setProperty('--space-8', '48px');

  // Bliss client surfaces remap --color-navy to espresso/black. Back-office
  // routes have already been re-surfaced onto pearl above, so skip the remap
  // there or the staff tools would snap back to black on blissbunkbeds.com.
  if (typeof window !== 'undefined' && !backOffice) {
    applyBlissClientColorTokens(window.location.hostname, pathname);
  }
}

// =============================================================================
// BACK OFFICE SURFACE — pearl, white and gold everywhere staff work
//
// Every internal tool (sales, operations, millworks, admin, partner studio)
// renders on the same pearl stage with white cards. The tenant's accent color
// still comes through — gold for Bliss, orange for Realtimax, maroon for RF —
// but no tenant gets a black or near-black back office.
// =============================================================================

/** Route prefixes that render the internal back office rather than a client-
 *  facing page. Matched against location.pathname. */
export const BACK_OFFICE_PATHS = [
  '/sales',
  '/operations',
  '/millworks',
  '/cutlist',
  '/admin',
  '/basic-admin',
  '/platform',
  '/plugins',
  '/find-proposal',
  '/owner',
] as const;

export function isBackOfficeSurface(pathname?: string | null): boolean {
  const p = (pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '')) || '';
  return BACK_OFFICE_PATHS.some((base) => p === base || p.startsWith(`${base}/`));
}

/** WCAG relative luminance, 0 (black) → 1 (white). */
function relativeLuminance(hex: string): number {
  const cleaned = hex.replace('#', '');
  const channel = (offset: number) => {
    const v = parseInt(cleaned.substring(offset, offset + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/**
 * Darkens an accent until it is legible against a pearl/white surface.
 * A gold tuned for black (#c9a959) washes out on white; this walks it down to
 * the same weight the hand-built Bliss Basic studio uses (#b8943f / #8a7030).
 */
function deepenForLightSurface(hex: string, maxLuminance: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  let out = hex;
  for (let i = 0; i < 12 && relativeLuminance(out) > maxLuminance; i++) {
    out = darken(out, 0.08);
  }
  return out;
}

/**
 * Rebuilds a theme on the pearl stage, keeping only its accent color.
 * A theme that is already light passes through untouched.
 */
export function toPearlSurface(theme: TenantTheme): TenantTheme {
  if (theme.mode === 'light') return theme;
  const pearl = THEME_PRESETS['pearl-gold'];
  // --color-gold does double duty as a fill and as accent text, so it gets the
  // mid weight; --color-gold-dark is the text-only weight and goes deeper.
  const primary = deepenForLightSurface(theme.primary, 0.26);
  return {
    ...pearl,
    label: `${theme.label} (Pearl)`,
    primary,
    primaryLight: deepenForLightSurface(theme.primaryLight, 0.45),
    primaryDark: deepenForLightSurface(theme.primaryDark, 0.16),
    primaryRgb: hexToRgb(primary),
  };
}

/**
 * Maps a hostname to the right preset for that domain.
 *
 * - *.realtimax.com  → realtimax (umbrella)
 * - *.blissbunkbeds.com  → classic-gold (Bliss)
 * - localhost / unknown  → DEFAULT_THEME_PRESET (umbrella)
 *
 * Used pre-auth (login screens, public pages) and as the fallback when
 * tenant resolution fails. Once a tenant is loaded from the DB, that
 * tenant's own theme_preset / primary_color overrides this.
 *
 * Add a new tenant by:
 *   1. Adding a new entry to THEME_PRESETS with its 7 base + 5 optional fields.
 *   2. Adding a hostname match here.
 *   3. (Optional) Setting tenants.theme_preset for tenants on that domain.
 */
export function getThemeForHost(hostname: string): ThemePresetId {
  const h = (hostname || '').toLowerCase();
  if (h.includes('blissbunkbeds') || h.includes('bliss')) return 'classic-gold';
  if (h.includes('realtimax')) return 'realtimax';
  return DEFAULT_THEME_PRESET;
}

/** Apply host brand colors synchronously — before React/tenant fetch — to avoid wrong-theme flash. */
export function bootstrapHostTheme(): void {
  if (typeof window === 'undefined') return;
  applyThemeToDocument(resolveTheme(getThemeForHost(window.location.hostname), null));
}
